/**
 * adminFrontPageI18n.js
 * Phase 7g.15.48 — Rick directive 2026-05-27.
 *
 * Translation keys for the SIX admin front-page surfaces:
 *   1. AdminDashboardHome.jsx          (admin landing — already partly i18n'd via LanguageContext)
 *   2. DailyRevenueGoalTracker.jsx     ("Daily Revenue Goal" widget)
 *   3. LiveRevenueTicker.jsx           ("LIVE REVENUE FEED" banner)
 *   4. BookingActivityFeed.jsx         (real-time event feed)
 *   5. AdminWebsiteLeads.jsx           ("Website Leads" page)
 *   6. SystemControlCenter.jsx         ("Control Center" header + tabs)
 *
 * Translations cover EN, ES, FR, DE, PT, ZH, JA, KO.
 *
 * Wired into LanguageContext.jsx via an additional fallback so `t(key)`
 * resolves these keys without bloating the main translations dictionary.
 *
 * IMPORTANT: these keys are scoped to the FRONT PAGE of the admin only.
 * Full-platform admin i18n is staged for Phase B/C/D.
 */
const ADMIN_FRONT_PAGE_I18N = {
  en: {
    // ── Daily Revenue Goal ─────────────────────────────────
    drg_title: "Daily Revenue Goal",
    drg_msg_achieved: "Goal Achieved!",
    drg_msg_almost: "Almost there!",
    drg_msg_on_track: "On track",
    drg_msg_keep_pushing: "Keep pushing",
    drg_set_daily_goal: "Set Daily Goal",
    drg_remaining: "Remaining",
    drg_per_hour: "Per Hour",
    drg_eta: "ETA",
    drg_vs_yesterday: "vs Yesterday",

    // ── Live Revenue Feed (ticker) ─────────────────────────
    lrt_title: "LIVE REVENUE FEED",
    lrt_sound_on: "$200+ alerts ON",
    lrt_sound_off: "$200+ alerts OFF",
    lrt_tooltip_sound_on: "Toggle high-value alerts",
    lrt_tooltip_sound_off: "Toggle high-value alerts",
    lrt_today_revenue: "Today",
    lrt_today_count: "Bookings",
    lrt_hour_revenue: "This Hour",
    lrt_pause: "Pause",
    lrt_resume: "Resume",
    lrt_no_transactions: "No transactions yet",
    lrt_high_value_toast: "High-Value Order",

    // ── Booking Activity Feed ──────────────────────────────
    baf_title: "Booking Activity Feed",
    baf_live: "LIVE",
    baf_sound_settings: "Sound Settings",
    baf_sound_alerts: "Sound Alerts",
    baf_sound_on: "ON",
    baf_sound_off: "OFF",
    baf_different_sounds: "Different sounds for each event:",
    baf_payments: "Payments: Cha-ching",
    baf_new_booking: "New booking: Alert",
    baf_vendor: "Vendor: Soft ping",
    baf_alerts: "Alerts: Warning",
    baf_test_sounds: "Test sounds:",
    baf_test_payment: "Payment",
    baf_test_new: "New",
    baf_test_complete: "Complete",
    baf_test_alert: "Alert",
    baf_refresh: "Refresh",
    baf_no_activity: "No recent activity",
    baf_appear_real_time: "Events will appear here in real-time",
    baf_showing: "Showing",
    baf_recent_events: "recent events",
    baf_view_all_activity: "View All Activity",
    baf_events_24h: "events (24h)",
    baf_connected: "connected",
    baf_event_new_booking: "New Booking",
    baf_event_payment: "Payment Received",
    baf_event_vendor_assigned: "Vendor Assigned",
    baf_event_status_changed: "Status Changed",
    baf_event_cancelled: "Cancelled",
    baf_event_offer_sent: "Offer Sent",
    baf_event_offer_accepted: "Offer Accepted",
    baf_event_offer_declined: "Offer Declined",
    baf_event_offer_timeout: "Offer Timed Out",
    baf_event_en_route: "En Route",
    baf_event_arrived: "Arrived",
    baf_event_location_update: "Location Update",
    baf_event_service_started: "Service Started",
    baf_event_service_completed: "Service Completed",
    baf_event_customer_confirmed: "Customer Confirmed",
    baf_event_tip_added: "Tip Added",
    baf_event_tip_reminder: "Tip Reminder",
    baf_event_payout_approved: "Payout Approved",
    baf_event_payout_completed: "Payout Complete",
    baf_event_dispute_opened: "Dispute Opened",
    baf_relative_just_now: "just now",
    baf_customer_label: "Customer",
    baf_vendor_label: "Vendor",

    // ── Website Leads ──────────────────────────────────────
    wl_title: "Website Leads",
    wl_subtitle: "Inbound from washpros.io — synced via Zapier",
    wl_refresh: "Refresh",
    wl_total: "Total",
    wl_today: "Today",
    wl_new: "New",
    wl_contacted: "Contacted",
    wl_converted: "Converted",
    wl_archived: "Archived",
    wl_response_time_tracker: "Response Time Tracker",
    wl_avg_response: "Avg Response",
    wl_fastest: "Fastest",
    wl_median: "Median",
    wl_slowest: "Slowest",
    wl_pending_label: "awaiting response",
    wl_trend_label: "Daily Avg Response Time (last 30 days)",
    wl_simulator: "Lead Score Simulator",
    wl_simulator_hint: "Test scoring without storing",
    wl_form_type: "Form Type",
    wl_name: "Name",
    wl_email: "Email",
    wl_phone: "Phone",
    wl_organization: "Organization",
    wl_message: "Message (vehicle keywords are scored)",
    wl_simulate: "Simulate",
    wl_search_placeholder: "Search name, email, phone...",
    wl_all_types: "All Types",
    wl_all_statuses: "All Statuses",
    wl_sort_score: "Sort: Score",
    wl_sort_newest: "Sort: Newest",
    wl_filter_customer_waitlist: "Customer Waitlist",
    wl_filter_vendor_signup: "Vendor Signup",
    wl_filter_contact: "Contact Form",
    wl_filter_fleet_inquiry: "Fleet Inquiry",
    wl_no_leads: "No leads found",
    wl_no_leads_hint: "Point your Zapier webhook to the endpoint below",
    wl_th_date: "Date",
    wl_th_name: "Name",
    wl_th_email: "Email",
    wl_th_type: "Type",
    wl_th_score: "Score",
    wl_th_status: "Status",
    wl_th_actions: "Actions",
    wl_action_contacted: "Contacted",
    wl_action_convert: "Convert",
    wl_auto_converted: "Auto-converted",
    wl_webhook_endpoint: "Zapier / Formspree Webhook Endpoint",
    wl_lead_quick_action: "Website Leads",
    wl_lead_quick_action_sub: "washpros.io signups",

    // ── System Control Center ──────────────────────────────
    scc_title: "System Control Center",
    scc_subtitle: "Master admin backdoor - Full system control",
    scc_save_all: "Save All Changes",
    scc_admin_only_title: "Admin Access Only",
    scc_admin_only_desc: "Changes here affect the entire platform. Use caution when modifying production settings.",
    scc_tab_payments: "Payments",
    scc_tab_pricing: "Pricing",
    scc_tab_integrations: "Integrations",
    scc_tab_features: "Features",
    scc_tab_system: "System",
    scc_tab_team: "Team Access",
    scc_tab_users: "Users",
  },

  es: {
    drg_title: "Meta de Ingresos Diarios",
    drg_msg_achieved: "¡Meta alcanzada!",
    drg_msg_almost: "¡Casi lo logras!",
    drg_msg_on_track: "En camino",
    drg_msg_keep_pushing: "Sigue adelante",
    drg_set_daily_goal: "Establecer Meta Diaria",
    drg_remaining: "Restante",
    drg_per_hour: "Por Hora",
    drg_eta: "Tiempo Est.",
    drg_vs_yesterday: "vs Ayer",
    lrt_title: "FEED DE INGRESOS EN VIVO",
    lrt_sound_on: "Alertas $200+ ACTIVAS",
    lrt_sound_off: "Alertas $200+ DESACTIVADAS",
    lrt_tooltip_sound_on: "Activar/desactivar alertas",
    lrt_tooltip_sound_off: "Activar/desactivar alertas",
    lrt_today_revenue: "Hoy",
    lrt_today_count: "Reservas",
    lrt_hour_revenue: "Esta Hora",
    lrt_pause: "Pausar",
    lrt_resume: "Reanudar",
    lrt_no_transactions: "Sin transacciones aún",
    lrt_high_value_toast: "Pedido de Alto Valor",
    baf_title: "Feed de Actividad de Reservas",
    baf_live: "EN VIVO",
    baf_sound_settings: "Configuración de Sonido",
    baf_sound_alerts: "Alertas de Sonido",
    baf_sound_on: "ACTIVO",
    baf_sound_off: "INACTIVO",
    baf_different_sounds: "Diferentes sonidos para cada evento:",
    baf_payments: "Pagos: Cha-ching",
    baf_new_booking: "Nueva reserva: Alerta",
    baf_vendor: "Vendedor: Tono suave",
    baf_alerts: "Alertas: Advertencia",
    baf_test_sounds: "Probar sonidos:",
    baf_test_payment: "Pago",
    baf_test_new: "Nuevo",
    baf_test_complete: "Completo",
    baf_test_alert: "Alerta",
    baf_refresh: "Actualizar",
    baf_no_activity: "Sin actividad reciente",
    baf_appear_real_time: "Los eventos aparecerán aquí en tiempo real",
    baf_showing: "Mostrando",
    baf_recent_events: "eventos recientes",
    baf_view_all_activity: "Ver Toda la Actividad",
    baf_events_24h: "eventos (24h)",
    baf_connected: "conectados",
    baf_event_new_booking: "Nueva Reserva",
    baf_event_payment: "Pago Recibido",
    baf_event_vendor_assigned: "Vendedor Asignado",
    baf_event_status_changed: "Estado Cambiado",
    baf_event_cancelled: "Cancelada",
    baf_event_offer_sent: "Oferta Enviada",
    baf_event_offer_accepted: "Oferta Aceptada",
    baf_event_offer_declined: "Oferta Rechazada",
    baf_event_offer_timeout: "Oferta Expiró",
    baf_event_en_route: "En Camino",
    baf_event_arrived: "Llegó",
    baf_event_location_update: "Actualización de Ubicación",
    baf_event_service_started: "Servicio Iniciado",
    baf_event_service_completed: "Servicio Completado",
    baf_event_customer_confirmed: "Cliente Confirmó",
    baf_event_tip_added: "Propina Agregada",
    baf_event_tip_reminder: "Recordatorio de Propina",
    baf_event_payout_approved: "Pago Aprobado",
    baf_event_payout_completed: "Pago Completado",
    baf_event_dispute_opened: "Disputa Abierta",
    baf_relative_just_now: "ahora mismo",
    baf_customer_label: "Cliente",
    baf_vendor_label: "Vendedor",
    wl_title: "Leads del Sitio Web",
    wl_subtitle: "Entrantes de washpros.io — sincronizados vía Zapier",
    wl_refresh: "Actualizar",
    wl_total: "Total",
    wl_today: "Hoy",
    wl_new: "Nuevo",
    wl_contacted: "Contactado",
    wl_converted: "Convertido",
    wl_archived: "Archivado",
    wl_response_time_tracker: "Rastreador de Tiempo de Respuesta",
    wl_avg_response: "Respuesta Prom.",
    wl_fastest: "Más Rápido",
    wl_median: "Mediana",
    wl_slowest: "Más Lento",
    wl_pending_label: "esperando respuesta",
    wl_trend_label: "Tiempo de Respuesta Diario Prom. (últimos 30 días)",
    wl_simulator: "Simulador de Puntuación de Leads",
    wl_simulator_hint: "Prueba puntuación sin guardar",
    wl_form_type: "Tipo de Formulario",
    wl_name: "Nombre",
    wl_email: "Email",
    wl_phone: "Teléfono",
    wl_organization: "Organización",
    wl_message: "Mensaje (palabras clave de vehículos se puntúan)",
    wl_simulate: "Simular",
    wl_search_placeholder: "Buscar nombre, email, teléfono...",
    wl_all_types: "Todos los Tipos",
    wl_all_statuses: "Todos los Estados",
    wl_sort_score: "Ordenar: Puntuación",
    wl_sort_newest: "Ordenar: Más Reciente",
    wl_filter_customer_waitlist: "Lista de Espera Cliente",
    wl_filter_vendor_signup: "Registro de Vendedor",
    wl_filter_contact: "Formulario de Contacto",
    wl_filter_fleet_inquiry: "Consulta de Flota",
    wl_no_leads: "No se encontraron leads",
    wl_no_leads_hint: "Apunta tu webhook de Zapier al endpoint a continuación",
    wl_th_date: "Fecha",
    wl_th_name: "Nombre",
    wl_th_email: "Email",
    wl_th_type: "Tipo",
    wl_th_score: "Puntuación",
    wl_th_status: "Estado",
    wl_th_actions: "Acciones",
    wl_action_contacted: "Contactado",
    wl_action_convert: "Convertir",
    wl_auto_converted: "Auto-convertido",
    wl_webhook_endpoint: "Endpoint de Webhook Zapier / Formspree",
    wl_lead_quick_action: "Leads del Sitio Web",
    wl_lead_quick_action_sub: "registros de washpros.io",
    scc_title: "Centro de Control del Sistema",
    scc_subtitle: "Acceso maestro de administrador - Control total del sistema",
    scc_save_all: "Guardar Todos los Cambios",
    scc_admin_only_title: "Solo Acceso de Administrador",
    scc_admin_only_desc: "Los cambios aquí afectan toda la plataforma. Usa precaución al modificar la configuración de producción.",
    scc_tab_payments: "Pagos",
    scc_tab_pricing: "Precios",
    scc_tab_integrations: "Integraciones",
    scc_tab_features: "Funciones",
    scc_tab_system: "Sistema",
    scc_tab_team: "Acceso de Equipo",
    scc_tab_users: "Usuarios",
  },

  fr: {
    drg_title: "Objectif de Revenus Quotidien",
    drg_msg_achieved: "Objectif atteint !",
    drg_msg_almost: "Presque là !",
    drg_msg_on_track: "Sur la bonne voie",
    drg_msg_keep_pushing: "Continuez",
    drg_set_daily_goal: "Définir l'Objectif Quotidien",
    drg_remaining: "Restant",
    drg_per_hour: "Par Heure",
    drg_eta: "ETA",
    drg_vs_yesterday: "vs Hier",
    lrt_title: "FIL DE REVENUS EN DIRECT",
    lrt_sound_on: "Alertes 200$+ ACTIVES",
    lrt_sound_off: "Alertes 200$+ INACTIVES",
    lrt_tooltip_sound_on: "Activer/désactiver les alertes",
    lrt_tooltip_sound_off: "Activer/désactiver les alertes",
    lrt_today_revenue: "Aujourd'hui",
    lrt_today_count: "Réservations",
    lrt_hour_revenue: "Cette Heure",
    lrt_pause: "Pause",
    lrt_resume: "Reprendre",
    lrt_no_transactions: "Pas encore de transactions",
    lrt_high_value_toast: "Commande de Grande Valeur",
    baf_title: "Fil d'Activité des Réservations",
    baf_live: "EN DIRECT",
    baf_sound_settings: "Paramètres Sonores",
    baf_sound_alerts: "Alertes Sonores",
    baf_sound_on: "ACTIVÉ",
    baf_sound_off: "DÉSACTIVÉ",
    baf_different_sounds: "Différents sons pour chaque événement :",
    baf_payments: "Paiements : Cha-ching",
    baf_new_booking: "Nouvelle réservation : Alerte",
    baf_vendor: "Vendeur : Son doux",
    baf_alerts: "Alertes : Avertissement",
    baf_test_sounds: "Tester les sons :",
    baf_test_payment: "Paiement",
    baf_test_new: "Nouveau",
    baf_test_complete: "Terminé",
    baf_test_alert: "Alerte",
    baf_refresh: "Actualiser",
    baf_no_activity: "Aucune activité récente",
    baf_appear_real_time: "Les événements apparaîtront ici en temps réel",
    baf_showing: "Affichage de",
    baf_recent_events: "événements récents",
    baf_view_all_activity: "Voir Toute l'Activité",
    baf_events_24h: "événements (24h)",
    baf_connected: "connectés",
    baf_event_new_booking: "Nouvelle Réservation",
    baf_event_payment: "Paiement Reçu",
    baf_event_vendor_assigned: "Vendeur Assigné",
    baf_event_status_changed: "Statut Modifié",
    baf_event_cancelled: "Annulée",
    baf_event_offer_sent: "Offre Envoyée",
    baf_event_offer_accepted: "Offre Acceptée",
    baf_event_offer_declined: "Offre Refusée",
    baf_event_offer_timeout: "Offre Expirée",
    baf_event_en_route: "En Route",
    baf_event_arrived: "Arrivé",
    baf_event_location_update: "Mise à Jour de Position",
    baf_event_service_started: "Service Démarré",
    baf_event_service_completed: "Service Terminé",
    baf_event_customer_confirmed: "Client Confirmé",
    baf_event_tip_added: "Pourboire Ajouté",
    baf_event_tip_reminder: "Rappel de Pourboire",
    baf_event_payout_approved: "Paiement Approuvé",
    baf_event_payout_completed: "Paiement Terminé",
    baf_event_dispute_opened: "Litige Ouvert",
    baf_relative_just_now: "à l'instant",
    baf_customer_label: "Client",
    baf_vendor_label: "Vendeur",
    wl_title: "Prospects du Site Web",
    wl_subtitle: "Entrants depuis washpros.io — synchronisés via Zapier",
    wl_refresh: "Actualiser",
    wl_total: "Total",
    wl_today: "Aujourd'hui",
    wl_new: "Nouveau",
    wl_contacted: "Contacté",
    wl_converted: "Converti",
    wl_archived: "Archivé",
    wl_response_time_tracker: "Suivi du Temps de Réponse",
    wl_avg_response: "Réponse Moy.",
    wl_fastest: "Plus Rapide",
    wl_median: "Médiane",
    wl_slowest: "Plus Lent",
    wl_pending_label: "en attente de réponse",
    wl_trend_label: "Temps de Réponse Moy. Quotidien (30 derniers jours)",
    wl_simulator: "Simulateur de Score de Lead",
    wl_simulator_hint: "Tester le scoring sans stocker",
    wl_form_type: "Type de Formulaire",
    wl_name: "Nom",
    wl_email: "Email",
    wl_phone: "Téléphone",
    wl_organization: "Organisation",
    wl_message: "Message (les mots-clés véhicules sont scorés)",
    wl_simulate: "Simuler",
    wl_search_placeholder: "Rechercher nom, email, téléphone...",
    wl_all_types: "Tous les Types",
    wl_all_statuses: "Tous les Statuts",
    wl_sort_score: "Trier : Score",
    wl_sort_newest: "Trier : Plus Récent",
    wl_filter_customer_waitlist: "Liste d'Attente Client",
    wl_filter_vendor_signup: "Inscription Vendeur",
    wl_filter_contact: "Formulaire de Contact",
    wl_filter_fleet_inquiry: "Demande Flotte",
    wl_no_leads: "Aucun lead trouvé",
    wl_no_leads_hint: "Pointez votre webhook Zapier vers le endpoint ci-dessous",
    wl_th_date: "Date",
    wl_th_name: "Nom",
    wl_th_email: "Email",
    wl_th_type: "Type",
    wl_th_score: "Score",
    wl_th_status: "Statut",
    wl_th_actions: "Actions",
    wl_action_contacted: "Contacté",
    wl_action_convert: "Convertir",
    wl_auto_converted: "Auto-converti",
    wl_webhook_endpoint: "Endpoint Webhook Zapier / Formspree",
    wl_lead_quick_action: "Prospects du Site",
    wl_lead_quick_action_sub: "inscriptions washpros.io",
    scc_title: "Centre de Contrôle Système",
    scc_subtitle: "Accès admin principal - Contrôle total du système",
    scc_save_all: "Enregistrer Toutes les Modifications",
    scc_admin_only_title: "Accès Admin Uniquement",
    scc_admin_only_desc: "Les modifications ici affectent toute la plateforme. Soyez prudent en modifiant les paramètres de production.",
    scc_tab_payments: "Paiements",
    scc_tab_pricing: "Tarification",
    scc_tab_integrations: "Intégrations",
    scc_tab_features: "Fonctionnalités",
    scc_tab_system: "Système",
    scc_tab_team: "Accès Équipe",
    scc_tab_users: "Utilisateurs",
  },

  de: {
    drg_title: "Tägliches Umsatzziel",
    drg_msg_achieved: "Ziel erreicht!",
    drg_msg_almost: "Fast geschafft!",
    drg_msg_on_track: "Auf Kurs",
    drg_msg_keep_pushing: "Weiter so",
    drg_set_daily_goal: "Tägliches Ziel festlegen",
    drg_remaining: "Verbleibend",
    drg_per_hour: "Pro Stunde",
    drg_eta: "ETA",
    drg_vs_yesterday: "vs Gestern",
    lrt_title: "LIVE-UMSATZ-FEED",
    lrt_sound_on: "200$+ Warnungen AN",
    lrt_sound_off: "200$+ Warnungen AUS",
    lrt_tooltip_sound_on: "Warnungen ein-/ausschalten",
    lrt_tooltip_sound_off: "Warnungen ein-/ausschalten",
    lrt_today_revenue: "Heute",
    lrt_today_count: "Buchungen",
    lrt_hour_revenue: "Diese Stunde",
    lrt_pause: "Pause",
    lrt_resume: "Fortsetzen",
    lrt_no_transactions: "Noch keine Transaktionen",
    lrt_high_value_toast: "Hochwertige Bestellung",
    baf_title: "Buchungsaktivitäten-Feed",
    baf_live: "LIVE",
    baf_sound_settings: "Sound-Einstellungen",
    baf_sound_alerts: "Sound-Warnungen",
    baf_sound_on: "EIN",
    baf_sound_off: "AUS",
    baf_different_sounds: "Verschiedene Sounds für jedes Ereignis:",
    baf_payments: "Zahlungen: Cha-ching",
    baf_new_booking: "Neue Buchung: Warnung",
    baf_vendor: "Anbieter: Sanftes Pingen",
    baf_alerts: "Warnungen: Warnsignal",
    baf_test_sounds: "Sounds testen:",
    baf_test_payment: "Zahlung",
    baf_test_new: "Neu",
    baf_test_complete: "Abgeschlossen",
    baf_test_alert: "Warnung",
    baf_refresh: "Aktualisieren",
    baf_no_activity: "Keine aktuelle Aktivität",
    baf_appear_real_time: "Ereignisse werden hier in Echtzeit angezeigt",
    baf_showing: "Anzeige von",
    baf_recent_events: "letzte Ereignisse",
    baf_view_all_activity: "Alle Aktivitäten anzeigen",
    baf_events_24h: "Ereignisse (24h)",
    baf_connected: "verbunden",
    baf_event_new_booking: "Neue Buchung",
    baf_event_payment: "Zahlung Erhalten",
    baf_event_vendor_assigned: "Anbieter Zugewiesen",
    baf_event_status_changed: "Status Geändert",
    baf_event_cancelled: "Storniert",
    baf_event_offer_sent: "Angebot Gesendet",
    baf_event_offer_accepted: "Angebot Angenommen",
    baf_event_offer_declined: "Angebot Abgelehnt",
    baf_event_offer_timeout: "Angebot Abgelaufen",
    baf_event_en_route: "Unterwegs",
    baf_event_arrived: "Angekommen",
    baf_event_location_update: "Standort-Update",
    baf_event_service_started: "Service Gestartet",
    baf_event_service_completed: "Service Abgeschlossen",
    baf_event_customer_confirmed: "Kunde Bestätigt",
    baf_event_tip_added: "Trinkgeld Hinzugefügt",
    baf_event_tip_reminder: "Trinkgeld-Erinnerung",
    baf_event_payout_approved: "Auszahlung Genehmigt",
    baf_event_payout_completed: "Auszahlung Abgeschlossen",
    baf_event_dispute_opened: "Streitfall Eröffnet",
    baf_relative_just_now: "gerade eben",
    baf_customer_label: "Kunde",
    baf_vendor_label: "Anbieter",
    wl_title: "Website-Leads",
    wl_subtitle: "Eingehend von washpros.io — synchronisiert via Zapier",
    wl_refresh: "Aktualisieren",
    wl_total: "Gesamt",
    wl_today: "Heute",
    wl_new: "Neu",
    wl_contacted: "Kontaktiert",
    wl_converted: "Konvertiert",
    wl_archived: "Archiviert",
    wl_response_time_tracker: "Reaktionszeit-Tracker",
    wl_avg_response: "Durchschn. Antwort",
    wl_fastest: "Schnellste",
    wl_median: "Median",
    wl_slowest: "Langsamste",
    wl_pending_label: "wartet auf Antwort",
    wl_trend_label: "Tägliche Durchschn. Reaktionszeit (letzte 30 Tage)",
    wl_simulator: "Lead-Score-Simulator",
    wl_simulator_hint: "Scoring testen ohne zu speichern",
    wl_form_type: "Formulartyp",
    wl_name: "Name",
    wl_email: "E-Mail",
    wl_phone: "Telefon",
    wl_organization: "Organisation",
    wl_message: "Nachricht (Fahrzeug-Schlüsselwörter werden gescort)",
    wl_simulate: "Simulieren",
    wl_search_placeholder: "Name, E-Mail, Telefon suchen...",
    wl_all_types: "Alle Typen",
    wl_all_statuses: "Alle Status",
    wl_sort_score: "Sortieren: Score",
    wl_sort_newest: "Sortieren: Neueste",
    wl_filter_customer_waitlist: "Kunden-Warteliste",
    wl_filter_vendor_signup: "Anbieter-Registrierung",
    wl_filter_contact: "Kontaktformular",
    wl_filter_fleet_inquiry: "Flotten-Anfrage",
    wl_no_leads: "Keine Leads gefunden",
    wl_no_leads_hint: "Zeigen Sie Ihren Zapier-Webhook auf den Endpunkt unten",
    wl_th_date: "Datum",
    wl_th_name: "Name",
    wl_th_email: "E-Mail",
    wl_th_type: "Typ",
    wl_th_score: "Score",
    wl_th_status: "Status",
    wl_th_actions: "Aktionen",
    wl_action_contacted: "Kontaktiert",
    wl_action_convert: "Konvertieren",
    wl_auto_converted: "Auto-konvertiert",
    wl_webhook_endpoint: "Zapier / Formspree Webhook-Endpunkt",
    wl_lead_quick_action: "Website-Leads",
    wl_lead_quick_action_sub: "washpros.io Anmeldungen",
    scc_title: "Systemsteuerungszentrum",
    scc_subtitle: "Hauptadmin-Backdoor - Vollständige Systemkontrolle",
    scc_save_all: "Alle Änderungen Speichern",
    scc_admin_only_title: "Nur Admin-Zugriff",
    scc_admin_only_desc: "Änderungen hier wirken sich auf die gesamte Plattform aus. Seien Sie vorsichtig bei der Änderung von Produktionseinstellungen.",
    scc_tab_payments: "Zahlungen",
    scc_tab_pricing: "Preisgestaltung",
    scc_tab_integrations: "Integrationen",
    scc_tab_features: "Funktionen",
    scc_tab_system: "System",
    scc_tab_team: "Team-Zugriff",
    scc_tab_users: "Benutzer",
  },

  pt: {
    drg_title: "Meta de Receita Diária",
    drg_msg_achieved: "Meta alcançada!",
    drg_msg_almost: "Quase lá!",
    drg_msg_on_track: "No caminho",
    drg_msg_keep_pushing: "Continue",
    drg_set_daily_goal: "Definir Meta Diária",
    drg_remaining: "Restante",
    drg_per_hour: "Por Hora",
    drg_eta: "Tempo Est.",
    drg_vs_yesterday: "vs Ontem",
    lrt_title: "FEED DE RECEITA AO VIVO",
    lrt_sound_on: "Alertas $200+ ATIVOS",
    lrt_sound_off: "Alertas $200+ DESATIVADOS",
    lrt_tooltip_sound_on: "Ativar/desativar alertas",
    lrt_tooltip_sound_off: "Ativar/desativar alertas",
    lrt_today_revenue: "Hoje",
    lrt_today_count: "Reservas",
    lrt_hour_revenue: "Esta Hora",
    lrt_pause: "Pausar",
    lrt_resume: "Retomar",
    lrt_no_transactions: "Sem transações ainda",
    lrt_high_value_toast: "Pedido de Alto Valor",
    baf_title: "Feed de Atividade de Reservas",
    baf_live: "AO VIVO",
    baf_sound_settings: "Configurações de Som",
    baf_sound_alerts: "Alertas Sonoros",
    baf_sound_on: "ATIVO",
    baf_sound_off: "INATIVO",
    baf_different_sounds: "Sons diferentes para cada evento:",
    baf_payments: "Pagamentos: Cha-ching",
    baf_new_booking: "Nova reserva: Alerta",
    baf_vendor: "Vendedor: Tom suave",
    baf_alerts: "Alertas: Aviso",
    baf_test_sounds: "Testar sons:",
    baf_test_payment: "Pagamento",
    baf_test_new: "Novo",
    baf_test_complete: "Completo",
    baf_test_alert: "Alerta",
    baf_refresh: "Atualizar",
    baf_no_activity: "Sem atividade recente",
    baf_appear_real_time: "Os eventos aparecerão aqui em tempo real",
    baf_showing: "Mostrando",
    baf_recent_events: "eventos recentes",
    baf_view_all_activity: "Ver Toda a Atividade",
    baf_events_24h: "eventos (24h)",
    baf_connected: "conectados",
    baf_event_new_booking: "Nova Reserva",
    baf_event_payment: "Pagamento Recebido",
    baf_event_vendor_assigned: "Vendedor Atribuído",
    baf_event_status_changed: "Status Alterado",
    baf_event_cancelled: "Cancelada",
    baf_event_offer_sent: "Oferta Enviada",
    baf_event_offer_accepted: "Oferta Aceita",
    baf_event_offer_declined: "Oferta Rejeitada",
    baf_event_offer_timeout: "Oferta Expirou",
    baf_event_en_route: "A Caminho",
    baf_event_arrived: "Chegou",
    baf_event_location_update: "Atualização de Localização",
    baf_event_service_started: "Serviço Iniciado",
    baf_event_service_completed: "Serviço Completado",
    baf_event_customer_confirmed: "Cliente Confirmou",
    baf_event_tip_added: "Gorjeta Adicionada",
    baf_event_tip_reminder: "Lembrete de Gorjeta",
    baf_event_payout_approved: "Pagamento Aprovado",
    baf_event_payout_completed: "Pagamento Completado",
    baf_event_dispute_opened: "Disputa Aberta",
    baf_relative_just_now: "agora mesmo",
    baf_customer_label: "Cliente",
    baf_vendor_label: "Vendedor",
    wl_title: "Leads do Site",
    wl_subtitle: "Entrantes de washpros.io — sincronizados via Zapier",
    wl_refresh: "Atualizar",
    wl_total: "Total",
    wl_today: "Hoje",
    wl_new: "Novo",
    wl_contacted: "Contatado",
    wl_converted: "Convertido",
    wl_archived: "Arquivado",
    wl_response_time_tracker: "Rastreador de Tempo de Resposta",
    wl_avg_response: "Resposta Méd.",
    wl_fastest: "Mais Rápido",
    wl_median: "Mediana",
    wl_slowest: "Mais Lento",
    wl_pending_label: "aguardando resposta",
    wl_trend_label: "Tempo de Resposta Méd. Diário (últimos 30 dias)",
    wl_simulator: "Simulador de Pontuação de Leads",
    wl_simulator_hint: "Testar pontuação sem armazenar",
    wl_form_type: "Tipo de Formulário",
    wl_name: "Nome",
    wl_email: "Email",
    wl_phone: "Telefone",
    wl_organization: "Organização",
    wl_message: "Mensagem (palavras-chave de veículos são pontuadas)",
    wl_simulate: "Simular",
    wl_search_placeholder: "Buscar nome, email, telefone...",
    wl_all_types: "Todos os Tipos",
    wl_all_statuses: "Todos os Status",
    wl_sort_score: "Ordenar: Pontuação",
    wl_sort_newest: "Ordenar: Mais Recente",
    wl_filter_customer_waitlist: "Lista de Espera Cliente",
    wl_filter_vendor_signup: "Cadastro de Vendedor",
    wl_filter_contact: "Formulário de Contato",
    wl_filter_fleet_inquiry: "Consulta de Frota",
    wl_no_leads: "Nenhum lead encontrado",
    wl_no_leads_hint: "Aponte seu webhook Zapier para o endpoint abaixo",
    wl_th_date: "Data",
    wl_th_name: "Nome",
    wl_th_email: "Email",
    wl_th_type: "Tipo",
    wl_th_score: "Pontuação",
    wl_th_status: "Status",
    wl_th_actions: "Ações",
    wl_action_contacted: "Contatado",
    wl_action_convert: "Converter",
    wl_auto_converted: "Auto-convertido",
    wl_webhook_endpoint: "Endpoint Webhook Zapier / Formspree",
    wl_lead_quick_action: "Leads do Site",
    wl_lead_quick_action_sub: "cadastros washpros.io",
    scc_title: "Centro de Controle do Sistema",
    scc_subtitle: "Acesso de admin mestre - Controle total do sistema",
    scc_save_all: "Salvar Todas as Alterações",
    scc_admin_only_title: "Apenas Acesso de Admin",
    scc_admin_only_desc: "Alterações aqui afetam toda a plataforma. Use cautela ao modificar configurações de produção.",
    scc_tab_payments: "Pagamentos",
    scc_tab_pricing: "Preços",
    scc_tab_integrations: "Integrações",
    scc_tab_features: "Recursos",
    scc_tab_system: "Sistema",
    scc_tab_team: "Acesso de Equipe",
    scc_tab_users: "Usuários",
  },

  zh: {
    drg_title: "每日收入目标",
    drg_msg_achieved: "目标达成！",
    drg_msg_almost: "即将达成！",
    drg_msg_on_track: "进展顺利",
    drg_msg_keep_pushing: "继续努力",
    drg_set_daily_goal: "设置每日目标",
    drg_remaining: "剩余",
    drg_per_hour: "每小时",
    drg_eta: "预计时间",
    drg_vs_yesterday: "vs 昨日",
    lrt_title: "实时收入动态",
    lrt_sound_on: "$200+ 提醒 已开启",
    lrt_sound_off: "$200+ 提醒 已关闭",
    lrt_tooltip_sound_on: "切换高价值提醒",
    lrt_tooltip_sound_off: "切换高价值提醒",
    lrt_today_revenue: "今日",
    lrt_today_count: "预订",
    lrt_hour_revenue: "本小时",
    lrt_pause: "暂停",
    lrt_resume: "继续",
    lrt_no_transactions: "暂无交易",
    lrt_high_value_toast: "高价值订单",
    baf_title: "预订活动动态",
    baf_live: "实时",
    baf_sound_settings: "声音设置",
    baf_sound_alerts: "声音提醒",
    baf_sound_on: "开",
    baf_sound_off: "关",
    baf_different_sounds: "每个事件不同的声音：",
    baf_payments: "付款：叮当声",
    baf_new_booking: "新预订：警报",
    baf_vendor: "供应商：轻柔提示音",
    baf_alerts: "警报：警告",
    baf_test_sounds: "测试声音：",
    baf_test_payment: "付款",
    baf_test_new: "新",
    baf_test_complete: "完成",
    baf_test_alert: "警报",
    baf_refresh: "刷新",
    baf_no_activity: "暂无最近活动",
    baf_appear_real_time: "事件将实时显示在此处",
    baf_showing: "显示",
    baf_recent_events: "最近事件",
    baf_view_all_activity: "查看全部活动",
    baf_events_24h: "事件（24小时）",
    baf_connected: "已连接",
    baf_event_new_booking: "新预订",
    baf_event_payment: "已收款",
    baf_event_vendor_assigned: "已分配供应商",
    baf_event_status_changed: "状态已更改",
    baf_event_cancelled: "已取消",
    baf_event_offer_sent: "已发送报价",
    baf_event_offer_accepted: "报价已接受",
    baf_event_offer_declined: "报价已拒绝",
    baf_event_offer_timeout: "报价已超时",
    baf_event_en_route: "途中",
    baf_event_arrived: "已到达",
    baf_event_location_update: "位置更新",
    baf_event_service_started: "服务已开始",
    baf_event_service_completed: "服务已完成",
    baf_event_customer_confirmed: "客户已确认",
    baf_event_tip_added: "已添加小费",
    baf_event_tip_reminder: "小费提醒",
    baf_event_payout_approved: "支付已批准",
    baf_event_payout_completed: "支付已完成",
    baf_event_dispute_opened: "争议已开启",
    baf_relative_just_now: "刚刚",
    baf_customer_label: "客户",
    baf_vendor_label: "供应商",
    wl_title: "网站潜在客户",
    wl_subtitle: "来自 washpros.io — 通过 Zapier 同步",
    wl_refresh: "刷新",
    wl_total: "总计",
    wl_today: "今日",
    wl_new: "新",
    wl_contacted: "已联系",
    wl_converted: "已转化",
    wl_archived: "已归档",
    wl_response_time_tracker: "响应时间跟踪",
    wl_avg_response: "平均响应",
    wl_fastest: "最快",
    wl_median: "中位数",
    wl_slowest: "最慢",
    wl_pending_label: "等待响应",
    wl_trend_label: "每日平均响应时间（最近30天）",
    wl_simulator: "潜在客户评分模拟器",
    wl_simulator_hint: "测试评分而不存储",
    wl_form_type: "表单类型",
    wl_name: "姓名",
    wl_email: "邮箱",
    wl_phone: "电话",
    wl_organization: "组织",
    wl_message: "消息（车辆关键词将被评分）",
    wl_simulate: "模拟",
    wl_search_placeholder: "搜索姓名、邮箱、电话...",
    wl_all_types: "所有类型",
    wl_all_statuses: "所有状态",
    wl_sort_score: "排序：分数",
    wl_sort_newest: "排序：最新",
    wl_filter_customer_waitlist: "客户等待名单",
    wl_filter_vendor_signup: "供应商注册",
    wl_filter_contact: "联系表单",
    wl_filter_fleet_inquiry: "车队咨询",
    wl_no_leads: "未找到潜在客户",
    wl_no_leads_hint: "将您的 Zapier webhook 指向下面的端点",
    wl_th_date: "日期",
    wl_th_name: "姓名",
    wl_th_email: "邮箱",
    wl_th_type: "类型",
    wl_th_score: "分数",
    wl_th_status: "状态",
    wl_th_actions: "操作",
    wl_action_contacted: "已联系",
    wl_action_convert: "转化",
    wl_auto_converted: "自动转化",
    wl_webhook_endpoint: "Zapier / Formspree Webhook 端点",
    wl_lead_quick_action: "网站潜在客户",
    wl_lead_quick_action_sub: "washpros.io 注册",
    scc_title: "系统控制中心",
    scc_subtitle: "主管理员后门 - 完全系统控制",
    scc_save_all: "保存所有更改",
    scc_admin_only_title: "仅限管理员访问",
    scc_admin_only_desc: "此处的更改会影响整个平台。修改生产设置时请谨慎。",
    scc_tab_payments: "付款",
    scc_tab_pricing: "定价",
    scc_tab_integrations: "集成",
    scc_tab_features: "功能",
    scc_tab_system: "系统",
    scc_tab_team: "团队访问",
    scc_tab_users: "用户",
  },

  ja: {
    drg_title: "日次収益目標",
    drg_msg_achieved: "目標達成！",
    drg_msg_almost: "もうすぐ！",
    drg_msg_on_track: "順調",
    drg_msg_keep_pushing: "頑張ろう",
    drg_set_daily_goal: "日次目標を設定",
    drg_remaining: "残り",
    drg_per_hour: "1時間あたり",
    drg_eta: "予想時間",
    drg_vs_yesterday: "昨日比",
    lrt_title: "ライブ収益フィード",
    lrt_sound_on: "$200+ アラート オン",
    lrt_sound_off: "$200+ アラート オフ",
    lrt_tooltip_sound_on: "高額アラート切替",
    lrt_tooltip_sound_off: "高額アラート切替",
    lrt_today_revenue: "今日",
    lrt_today_count: "予約",
    lrt_hour_revenue: "この時間",
    lrt_pause: "一時停止",
    lrt_resume: "再開",
    lrt_no_transactions: "取引なし",
    lrt_high_value_toast: "高額注文",
    baf_title: "予約アクティビティフィード",
    baf_live: "ライブ",
    baf_sound_settings: "サウンド設定",
    baf_sound_alerts: "サウンドアラート",
    baf_sound_on: "オン",
    baf_sound_off: "オフ",
    baf_different_sounds: "各イベントに異なる音：",
    baf_payments: "支払い：チャリン音",
    baf_new_booking: "新規予約：アラート",
    baf_vendor: "ベンダー：ソフトピン",
    baf_alerts: "アラート：警告",
    baf_test_sounds: "音をテスト：",
    baf_test_payment: "支払い",
    baf_test_new: "新規",
    baf_test_complete: "完了",
    baf_test_alert: "アラート",
    baf_refresh: "更新",
    baf_no_activity: "最近のアクティビティなし",
    baf_appear_real_time: "イベントはリアルタイムでここに表示されます",
    baf_showing: "表示中",
    baf_recent_events: "最近のイベント",
    baf_view_all_activity: "すべてのアクティビティを表示",
    baf_events_24h: "イベント（24時間）",
    baf_connected: "接続済み",
    baf_event_new_booking: "新規予約",
    baf_event_payment: "支払い受領",
    baf_event_vendor_assigned: "ベンダー割当",
    baf_event_status_changed: "ステータス変更",
    baf_event_cancelled: "キャンセル",
    baf_event_offer_sent: "オファー送信",
    baf_event_offer_accepted: "オファー承諾",
    baf_event_offer_declined: "オファー拒否",
    baf_event_offer_timeout: "オファータイムアウト",
    baf_event_en_route: "移動中",
    baf_event_arrived: "到着",
    baf_event_location_update: "位置情報更新",
    baf_event_service_started: "サービス開始",
    baf_event_service_completed: "サービス完了",
    baf_event_customer_confirmed: "顧客確認",
    baf_event_tip_added: "チップ追加",
    baf_event_tip_reminder: "チップリマインダー",
    baf_event_payout_approved: "支払い承認",
    baf_event_payout_completed: "支払い完了",
    baf_event_dispute_opened: "紛争開始",
    baf_relative_just_now: "たった今",
    baf_customer_label: "顧客",
    baf_vendor_label: "ベンダー",
    wl_title: "ウェブサイトリード",
    wl_subtitle: "washpros.io から — Zapier 経由で同期",
    wl_refresh: "更新",
    wl_total: "合計",
    wl_today: "今日",
    wl_new: "新規",
    wl_contacted: "連絡済み",
    wl_converted: "変換済み",
    wl_archived: "アーカイブ済み",
    wl_response_time_tracker: "応答時間トラッカー",
    wl_avg_response: "平均応答",
    wl_fastest: "最速",
    wl_median: "中央値",
    wl_slowest: "最遅",
    wl_pending_label: "応答待ち",
    wl_trend_label: "日次平均応答時間（過去30日）",
    wl_simulator: "リードスコアシミュレーター",
    wl_simulator_hint: "保存せずにスコアをテスト",
    wl_form_type: "フォームタイプ",
    wl_name: "名前",
    wl_email: "メール",
    wl_phone: "電話",
    wl_organization: "組織",
    wl_message: "メッセージ（車両キーワードはスコア化されます）",
    wl_simulate: "シミュレート",
    wl_search_placeholder: "名前、メール、電話を検索...",
    wl_all_types: "すべてのタイプ",
    wl_all_statuses: "すべてのステータス",
    wl_sort_score: "並び替え：スコア",
    wl_sort_newest: "並び替え：最新",
    wl_filter_customer_waitlist: "顧客ウェイトリスト",
    wl_filter_vendor_signup: "ベンダー登録",
    wl_filter_contact: "お問い合わせフォーム",
    wl_filter_fleet_inquiry: "フリート問い合わせ",
    wl_no_leads: "リードが見つかりません",
    wl_no_leads_hint: "Zapier webhook を以下のエンドポイントに向けてください",
    wl_th_date: "日付",
    wl_th_name: "名前",
    wl_th_email: "メール",
    wl_th_type: "タイプ",
    wl_th_score: "スコア",
    wl_th_status: "ステータス",
    wl_th_actions: "アクション",
    wl_action_contacted: "連絡済み",
    wl_action_convert: "変換",
    wl_auto_converted: "自動変換",
    wl_webhook_endpoint: "Zapier / Formspree Webhook エンドポイント",
    wl_lead_quick_action: "ウェブサイトリード",
    wl_lead_quick_action_sub: "washpros.io 登録",
    scc_title: "システム制御センター",
    scc_subtitle: "マスター管理者バックドア - 完全なシステム制御",
    scc_save_all: "すべての変更を保存",
    scc_admin_only_title: "管理者のみアクセス可能",
    scc_admin_only_desc: "ここでの変更はプラットフォーム全体に影響します。本番設定を変更する際は注意してください。",
    scc_tab_payments: "支払い",
    scc_tab_pricing: "価格",
    scc_tab_integrations: "統合",
    scc_tab_features: "機能",
    scc_tab_system: "システム",
    scc_tab_team: "チームアクセス",
    scc_tab_users: "ユーザー",
  },

  ko: {
    drg_title: "일일 수익 목표",
    drg_msg_achieved: "목표 달성!",
    drg_msg_almost: "거의 다 왔어요!",
    drg_msg_on_track: "순조롭게",
    drg_msg_keep_pushing: "계속 진행",
    drg_set_daily_goal: "일일 목표 설정",
    drg_remaining: "남음",
    drg_per_hour: "시간당",
    drg_eta: "예상 시간",
    drg_vs_yesterday: "어제 대비",
    lrt_title: "실시간 수익 피드",
    lrt_sound_on: "$200+ 알림 켜짐",
    lrt_sound_off: "$200+ 알림 꺼짐",
    lrt_tooltip_sound_on: "고가치 알림 전환",
    lrt_tooltip_sound_off: "고가치 알림 전환",
    lrt_today_revenue: "오늘",
    lrt_today_count: "예약",
    lrt_hour_revenue: "이번 시간",
    lrt_pause: "일시정지",
    lrt_resume: "재개",
    lrt_no_transactions: "거래 없음",
    lrt_high_value_toast: "고가치 주문",
    baf_title: "예약 활동 피드",
    baf_live: "라이브",
    baf_sound_settings: "사운드 설정",
    baf_sound_alerts: "사운드 알림",
    baf_sound_on: "켜짐",
    baf_sound_off: "꺼짐",
    baf_different_sounds: "각 이벤트별 다른 소리:",
    baf_payments: "결제: 차칭 소리",
    baf_new_booking: "새 예약: 알림",
    baf_vendor: "벤더: 부드러운 핑",
    baf_alerts: "알림: 경고",
    baf_test_sounds: "사운드 테스트:",
    baf_test_payment: "결제",
    baf_test_new: "신규",
    baf_test_complete: "완료",
    baf_test_alert: "알림",
    baf_refresh: "새로 고침",
    baf_no_activity: "최근 활동 없음",
    baf_appear_real_time: "이벤트는 여기에 실시간으로 표시됩니다",
    baf_showing: "표시",
    baf_recent_events: "최근 이벤트",
    baf_view_all_activity: "모든 활동 보기",
    baf_events_24h: "이벤트 (24시간)",
    baf_connected: "연결됨",
    baf_event_new_booking: "신규 예약",
    baf_event_payment: "결제 수신",
    baf_event_vendor_assigned: "벤더 할당",
    baf_event_status_changed: "상태 변경",
    baf_event_cancelled: "취소됨",
    baf_event_offer_sent: "오퍼 발송",
    baf_event_offer_accepted: "오퍼 수락",
    baf_event_offer_declined: "오퍼 거부",
    baf_event_offer_timeout: "오퍼 시간 초과",
    baf_event_en_route: "이동 중",
    baf_event_arrived: "도착",
    baf_event_location_update: "위치 업데이트",
    baf_event_service_started: "서비스 시작",
    baf_event_service_completed: "서비스 완료",
    baf_event_customer_confirmed: "고객 확인",
    baf_event_tip_added: "팁 추가",
    baf_event_tip_reminder: "팁 알림",
    baf_event_payout_approved: "지급 승인",
    baf_event_payout_completed: "지급 완료",
    baf_event_dispute_opened: "분쟁 개시",
    baf_relative_just_now: "방금",
    baf_customer_label: "고객",
    baf_vendor_label: "벤더",
    wl_title: "웹사이트 리드",
    wl_subtitle: "washpros.io에서 — Zapier로 동기화",
    wl_refresh: "새로 고침",
    wl_total: "전체",
    wl_today: "오늘",
    wl_new: "신규",
    wl_contacted: "연락됨",
    wl_converted: "전환됨",
    wl_archived: "보관됨",
    wl_response_time_tracker: "응답 시간 추적기",
    wl_avg_response: "평균 응답",
    wl_fastest: "최단",
    wl_median: "중앙값",
    wl_slowest: "최장",
    wl_pending_label: "응답 대기 중",
    wl_trend_label: "일일 평균 응답 시간 (최근 30일)",
    wl_simulator: "리드 점수 시뮬레이터",
    wl_simulator_hint: "저장 없이 점수 테스트",
    wl_form_type: "양식 유형",
    wl_name: "이름",
    wl_email: "이메일",
    wl_phone: "전화",
    wl_organization: "조직",
    wl_message: "메시지 (차량 키워드는 점수가 매겨집니다)",
    wl_simulate: "시뮬레이션",
    wl_search_placeholder: "이름, 이메일, 전화 검색...",
    wl_all_types: "모든 유형",
    wl_all_statuses: "모든 상태",
    wl_sort_score: "정렬: 점수",
    wl_sort_newest: "정렬: 최신",
    wl_filter_customer_waitlist: "고객 대기 명단",
    wl_filter_vendor_signup: "벤더 가입",
    wl_filter_contact: "문의 양식",
    wl_filter_fleet_inquiry: "플릿 문의",
    wl_no_leads: "리드를 찾을 수 없음",
    wl_no_leads_hint: "Zapier webhook을 아래 엔드포인트로 지정하세요",
    wl_th_date: "날짜",
    wl_th_name: "이름",
    wl_th_email: "이메일",
    wl_th_type: "유형",
    wl_th_score: "점수",
    wl_th_status: "상태",
    wl_th_actions: "작업",
    wl_action_contacted: "연락됨",
    wl_action_convert: "전환",
    wl_auto_converted: "자동 전환",
    wl_webhook_endpoint: "Zapier / Formspree Webhook 엔드포인트",
    wl_lead_quick_action: "웹사이트 리드",
    wl_lead_quick_action_sub: "washpros.io 가입",
    scc_title: "시스템 제어 센터",
    scc_subtitle: "마스터 관리자 백도어 - 전체 시스템 제어",
    scc_save_all: "모든 변경 사항 저장",
    scc_admin_only_title: "관리자 전용 접근",
    scc_admin_only_desc: "여기에서의 변경 사항은 전체 플랫폼에 영향을 미칩니다. 프로덕션 설정 수정 시 주의하세요.",
    scc_tab_payments: "결제",
    scc_tab_pricing: "가격",
    scc_tab_integrations: "통합",
    scc_tab_features: "기능",
    scc_tab_system: "시스템",
    scc_tab_team: "팀 액세스",
    scc_tab_users: "사용자",
  },
  // Phase i18n expansion 2026-06-12.
  it: {
    "drg_title": "Obiettivo di Ricavo Giornaliero",
    "drg_msg_achieved": "Obiettivo Raggiunto!",
    "drg_msg_almost": "Quasi fatto!",
    "drg_msg_on_track": "Sulla buona strada",
    "drg_msg_keep_pushing": "Continua così",
    "drg_set_daily_goal": "Imposta Obiettivo Giornaliero",
    "drg_remaining": "Rimanente",
    "drg_per_hour": "All'ora",
    "drg_eta": "ETA",
    "drg_vs_yesterday": "vs Ieri",
    "lrt_title": "FLUSSO DI RICAVI IN TEMPO REALE",
    "lrt_sound_on": "Avvisi $200+ ATTIVI",
    "lrt_sound_off": "Avvisi $200+ DISATTIVI",
    "lrt_tooltip_sound_on": "Attiva/disattiva avvisi di alto valore",
    "lrt_tooltip_sound_off": "Attiva/disattiva avvisi di alto valore",
    "lrt_today_revenue": "Oggi",
    "lrt_today_count": "Prenotazioni",
    "lrt_hour_revenue": "Quest'ora",
    "lrt_pause": "Pausa",
    "lrt_resume": "Riprendi",
    "lrt_no_transactions": "Nessuna transazione per ora",
    "lrt_high_value_toast": "Ordine di Alto Valore",
    "baf_title": "Flusso Attività Prenotazioni",
    "baf_live": "IN DIRETTA",
    "baf_sound_settings": "Impostazioni Suono",
    "baf_sound_alerts": "Avvisi Sonori",
    "baf_sound_on": "ATTIVO",
    "baf_sound_off": "DISATTIVO",
    "baf_different_sounds": "Suoni diversi per ogni evento:",
    "baf_payments": "Pagamenti: Cha-ching",
    "baf_new_booking": "Nuova prenotazione: Alert",
    "baf_vendor": "Venditore: Ping leggero",
    "baf_alerts": "Avvisi: Avvertimento",
    "baf_test_sounds": "Prova suoni:",
    "baf_test_payment": "Pagamento",
    "baf_test_new": "Nuovo",
    "baf_test_complete": "Completato",
    "baf_test_alert": "Avviso",
    "baf_refresh": "Aggiorna",
    "baf_no_activity": "Nessuna attività recente",
    "baf_appear_real_time": "Gli eventi appariranno qui in tempo reale",
    "baf_showing": "Mostrando",
    "baf_recent_events": "eventi recenti",
    "baf_view_all_activity": "Visualizza Tutte le Attività",
    "baf_events_24h": "eventi (24h)",
    "baf_connected": "connesso",
    "baf_event_new_booking": "Nuova Prenotazione",
    "baf_event_payment": "Pagamento Ricevuto",
    "baf_event_vendor_assigned": "Venditore Assegnato",
    "baf_event_status_changed": "Stato Modificato",
    "baf_event_cancelled": "Annullato",
    "baf_event_offer_sent": "Offerta Inviata",
    "baf_event_offer_accepted": "Offerta Accettata",
    "baf_event_offer_declined": "Offerta Rifiutata",
    "baf_event_offer_timeout": "Offerta Scaduta",
    "baf_event_en_route": "In Arrivo",
    "baf_event_arrived": "Arrivato",
    "baf_event_location_update": "Aggiornamento Posizione",
    "baf_event_service_started": "Servizio Iniziato",
    "baf_event_service_completed": "Servizio Completato",
    "baf_event_customer_confirmed": "Confermato dal Cliente",
    "baf_event_tip_added": "Mancia Aggiunta",
    "baf_event_tip_reminder": "Promemoria Mancia",
    "baf_event_payout_approved": "Pagamento Approvato",
    "baf_event_payout_completed": "Pagamento Completato",
    "baf_event_dispute_opened": "Contestazione Aperta",
    "baf_relative_just_now": "proprio ora",
    "baf_customer_label": "Cliente",
    "baf_vendor_label": "Fornitore",
    "wl_title": "Contatti dal Sito",
    "wl_subtitle": "Inbound da washpros.io — sincronizzato via Zapier",
    "wl_refresh": "Aggiorna",
    "wl_total": "Totale",
    "wl_today": "Oggi",
    "wl_new": "Nuovo",
    "wl_contacted": "Contattato",
    "wl_converted": "Convertito",
    "wl_archived": "Archiviato",
    "wl_response_time_tracker": "Tracciatore Tempi di Risposta",
    "wl_avg_response": "Risposta Media",
    "wl_fastest": "Più Veloce",
    "wl_median": "Mediana",
    "wl_slowest": "Più Lento",
    "wl_pending_label": "in attesa di risposta",
    "wl_trend_label": "Tempo Medio di Risposta Giornaliero (ultimi 30 giorni)",
    "wl_simulator": "Simulatore Punteggio Lead",
    "wl_simulator_hint": "Prova la valutazione senza memorizzare",
    "wl_form_type": "Tipo di Modulo",
    "wl_name": "Nome",
    "wl_email": "Email",
    "wl_phone": "Telefono",
    "wl_organization": "Organizzazione",
    "wl_message": "Messaggio (le parole chiave del veicolo vengono valutate)",
    "wl_simulate": "Simula",
    "wl_search_placeholder": "Cerca nome, email, telefono...",
    "wl_all_types": "Tutti i Tipi",
    "wl_all_statuses": "Tutti gli Stati",
    "wl_sort_score": "Ordina: Punteggio",
    "wl_sort_newest": "Ordina: Più Recenti",
    "wl_filter_customer_waitlist": "Lista d'Attesa Clienti",
    "wl_filter_vendor_signup": "Registrazione Fornitori",
    "wl_filter_contact": "Modulo di Contatto",
    "wl_filter_fleet_inquiry": "Richiesta Flotta",
    "wl_no_leads": "Nessun lead trovato",
    "wl_no_leads_hint": "Indirizza il tuo webhook Zapier al seguente endpoint",
    "wl_th_date": "Data",
    "wl_th_name": "Nome",
    "wl_th_email": "Email",
    "wl_th_type": "Tipo",
    "wl_th_score": "Punteggio",
    "wl_th_status": "Stato",
    "wl_th_actions": "Azioni",
    "wl_action_contacted": "Contattato",
    "wl_action_convert": "Converti",
    "wl_auto_converted": "Auto-convertito",
    "wl_webhook_endpoint": "Endpoint Webhook Zapier / Formspree",
    "wl_lead_quick_action": "Lead dal Sito",
    "wl_lead_quick_action_sub": "Iscrizioni tramite washpros.io",
    "scc_title": "Centro di Controllo Sistema",
    "scc_subtitle": "Accesso amministratore - Controllo completo del sistema",
    "scc_save_all": "Salva Tutte le Modifiche",
    "scc_admin_only_title": "Accesso Riservato agli Amministratori",
    "scc_admin_only_desc": "Le modifiche qui influenzano l'intera piattaforma. Usa cautela quando modifichi le impostazioni di produzione.",
    "scc_tab_payments": "Pagamenti",
    "scc_tab_pricing": "Prezzi",
    "scc_tab_integrations": "Integrazioni",
    "scc_tab_features": "Funzionalità",
    "scc_tab_system": "Sistema",
    "scc_tab_team": "Accesso al Team",
    "scc_tab_users": "Utenti"
  },
  // Phase i18n expansion 2026-06-12.
  ru: {
    "drg_title": "Ежедневная Цель Дохода",
    "drg_msg_achieved": "Цель Достигнута!",
    "drg_msg_almost": "Почти достигли!",
    "drg_msg_on_track": "На правильном пути",
    "drg_msg_keep_pushing": "Продолжайте в том же духе",
    "drg_set_daily_goal": "Установить Ежедневную Цель",
    "drg_remaining": "Осталось",
    "drg_per_hour": "В час",
    "drg_eta": "Осталось времени",
    "drg_vs_yesterday": "по сравнению со вчерашним днем",
    "lrt_title": "ЛЕНТА ДОХОДОВ В РЕАЛЬНОМ ВРЕМЕНИ",
    "lrt_sound_on": "Уведомления $200+ ВКЛ",
    "lrt_sound_off": "Уведомления $200+ ВЫКЛ",
    "lrt_tooltip_sound_on": "Переключить уведомления о крупных суммах",
    "lrt_tooltip_sound_off": "Переключить уведомления о крупных суммах",
    "lrt_today_revenue": "Сегодня",
    "lrt_today_count": "Бронирования",
    "lrt_hour_revenue": "Этот Час",
    "lrt_pause": "Пауза",
    "lrt_resume": "Продолжить",
    "lrt_no_transactions": "Транзакций пока нет",
    "lrt_high_value_toast": "Заказ на Большую Сумму",
    "baf_title": "Лента Активности Бронирований",
    "baf_live": "В РЕАЛЬНОМ ВРЕМЕНИ",
    "baf_sound_settings": "Настройки Звука",
    "baf_sound_alerts": "Звуковые Уведомления",
    "baf_sound_on": "ВКЛ",
    "baf_sound_off": "ВЫКЛ",
    "baf_different_sounds": "Разные звуки для каждого события:",
    "baf_payments": "Платежи: Звон монет",
    "baf_new_booking": "Новое бронирование: Уведомление",
    "baf_vendor": "Поставщик: Легкий сигнал",
    "baf_alerts": "Уведомления: Предупреждение",
    "baf_test_sounds": "Тест звуков:",
    "baf_test_payment": "Платеж",
    "baf_test_new": "Новое",
    "baf_test_complete": "Завершено",
    "baf_test_alert": "Уведомление",
    "baf_refresh": "Обновить",
    "baf_no_activity": "Недавней активности нет",
    "baf_appear_real_time": "События будут появляться здесь в реальном времени",
    "baf_showing": "Показано",
    "baf_recent_events": "последние события",
    "baf_view_all_activity": "Просмотреть Весь Актив",
    "baf_events_24h": "события (24 ч)",
    "baf_connected": "подключено",
    "baf_event_new_booking": "Новое Бронирование",
    "baf_event_payment": "Платеж Получен",
    "baf_event_vendor_assigned": "Назначен Поставщик",
    "baf_event_status_changed": "Статус Изменен",
    "baf_event_cancelled": "Отменено",
    "baf_event_offer_sent": "Предложение отправлено",
    "baf_event_offer_accepted": "Предложение принято",
    "baf_event_offer_declined": "Предложение отклонено",
    "baf_event_offer_timeout": "Время предложения истекло",
    "baf_event_en_route": "В пути",
    "baf_event_arrived": "Прибыл",
    "baf_event_location_update": "Обновление местоположения",
    "baf_event_service_started": "Сервис начат",
    "baf_event_service_completed": "Сервис завершён",
    "baf_event_customer_confirmed": "Подтверждено клиентом",
    "baf_event_tip_added": "Чаевые добавлены",
    "baf_event_tip_reminder": "Напоминание о чаевых",
    "baf_event_payout_approved": "Выплата утверждена",
    "baf_event_payout_completed": "Выплата завершена",
    "baf_event_dispute_opened": "Открыта претензия",
    "baf_relative_just_now": "только что",
    "baf_customer_label": "Клиент",
    "baf_vendor_label": "Поставщик",
    "wl_title": "Лиды c сайта",
    "wl_subtitle": "Входящие с washpros.io — синхронизация через Zapier",
    "wl_refresh": "Обновить",
    "wl_total": "Всего",
    "wl_today": "Сегодня",
    "wl_new": "Новые",
    "wl_contacted": "Связались",
    "wl_converted": "Конвертированы",
    "wl_archived": "В архиве",
    "wl_response_time_tracker": "Трекер времени ответа",
    "wl_avg_response": "Среднее время ответа",
    "wl_fastest": "Самый быстрый",
    "wl_median": "Медиана",
    "wl_slowest": "Самый медленный",
    "wl_pending_label": "ожидание ответа",
    "wl_trend_label": "Среднее время ответа за день (последние 30 дней)",
    "wl_simulator": "Симулятор оценки лида",
    "wl_simulator_hint": "Тестирование оценки без сохранения",
    "wl_form_type": "Тип формы",
    "wl_name": "Имя",
    "wl_email": "Электронная почта",
    "wl_phone": "Телефон",
    "wl_organization": "Организация",
    "wl_message": "Сообщение (ключевые слова транспортного средства оцениваются)",
    "wl_simulate": "Симулировать",
    "wl_search_placeholder": "Поиск по имени, email, телефону...",
    "wl_all_types": "Все типы",
    "wl_all_statuses": "Все статусы",
    "wl_sort_score": "Сортировать: оценка",
    "wl_sort_newest": "Сортировать: новейшие",
    "wl_filter_customer_waitlist": "Список ожидания клиентов",
    "wl_filter_vendor_signup": "Регистрация поставщика",
    "wl_filter_contact": "Форма обратной связи",
    "wl_filter_fleet_inquiry": "Запрос на обслуживание автопарка",
    "wl_no_leads": "Лидов не найдено",
    "wl_no_leads_hint": "Укажите ваш вебхук Zapier на конечную точку ниже",
    "wl_th_date": "Дата",
    "wl_th_name": "Имя",
    "wl_th_email": "Электронная почта",
    "wl_th_type": "Тип",
    "wl_th_score": "Оценка",
    "wl_th_status": "Статус",
    "wl_th_actions": "Действия",
    "wl_action_contacted": "Связались",
    "wl_action_convert": "Конвертировать",
    "wl_auto_converted": "Автоматически конвертирован",
    "wl_webhook_endpoint": "Конечная точка вебхука Zapier / Formspree",
    "wl_lead_quick_action": "Лиды с сайта",
    "wl_lead_quick_action_sub": "Регистрации через washpros.io",
    "scc_title": "Центр управления системой",
    "scc_subtitle": "Административная панель - Полный контроль системы",
    "scc_save_all": "Сохранить все изменения",
    "scc_admin_only_title": "Только для администраторов",
    "scc_admin_only_desc": "Изменения влияют на всю платформу. Будьте осторожны при изменении производственных настроек.",
    "scc_tab_payments": "Платежи",
    "scc_tab_pricing": "Цены",
    "scc_tab_integrations": "Интеграции",
    "scc_tab_features": "Функции",
    "scc_tab_system": "Система",
    "scc_tab_team": "Доступ команды",
    "scc_tab_users": "Пользователи"
  },
  // Phase i18n expansion 2026-06-12.
  ht: {
    "drg_title": "Objektif Revni Chak Jou",
    "drg_msg_achieved": "Objektif Reyalize!",
    "drg_msg_almost": "Preske rive!",
    "drg_msg_on_track": "Sou bon wout",
    "drg_msg_keep_pushing": "Kontinye fè efò",
    "drg_set_daily_goal": "Fikse Objektif Chak Jou",
    "drg_remaining": "Rete",
    "drg_per_hour": "Pa Lè",
    "drg_eta": "Antisipasyon Tan",
    "drg_vs_yesterday": "vs Ayè",
    "lrt_title": "ÈMÈSYON REVNI AN DIRÈK",
    "lrt_sound_on": "Alèt $200+ AKTIVE",
    "lrt_sound_off": "Alèt $200+ DEZAKTIVE",
    "lrt_tooltip_sound_on": "Chanje alèt valè-wo",
    "lrt_tooltip_sound_off": "Chanje alèt valè-wo",
    "lrt_today_revenue": "Jodi a",
    "lrt_today_count": "Rezèrvasyon",
    "lrt_hour_revenue": "Sa a Lè",
    "lrt_pause": "Pòz",
    "lrt_resume": "Kontinye",
    "lrt_no_transactions": "Pa gen tranzaksyon pou kounye a",
    "lrt_high_value_toast": "Kòmand Valè-Elev",
    "baf_title": "Fliye Aktivite Rezèrvasyon",
    "baf_live": "AN DIRÈK",
    "baf_sound_settings": "Anviwònman Son",
    "baf_sound_alerts": "Alèt Son",
    "baf_sound_on": "AKTIVE",
    "baf_sound_off": "DEZAKTIVE",
    "baf_different_sounds": "Son diferan pou chak evènman:",
    "baf_payments": "Peman: Cha-ching",
    "baf_new_booking": "Nouvo rezèrvasyon: Alèt",
    "baf_vendor": "Founisè: Ping dou",
    "baf_alerts": "Alèt: Avètisman",
    "baf_test_sounds": "Teste son yo:",
    "baf_test_payment": "Peman",
    "baf_test_new": "Nouvo",
    "baf_test_complete": "Fini",
    "baf_test_alert": "Alèt",
    "baf_refresh": "Rafrechi",
    "baf_no_activity": "Pa gen aktivite resan",
    "baf_appear_real_time": "Evènman yo ap parèt isit la an tan reyèl",
    "baf_showing": "Montre",
    "baf_recent_events": "evènman resan",
    "baf_view_all_activity": "Gade Tout Aktivite",
    "baf_events_24h": "evènman (24è)",
    "baf_connected": "konekte",
    "baf_event_new_booking": "Nouvo Rezèrvasyon",
    "baf_event_payment": "Resevwa Peman",
    "baf_event_vendor_assigned": "Founisè Asiyen",
    "baf_event_status_changed": "Estati Chanje",
    "baf_event_cancelled": "Anile",
    "baf_event_offer_sent": "Ofè Voye",
    "baf_event_offer_accepted": "Ofè Aksepte",
    "baf_event_offer_declined": "Ofè Refize",
    "baf_event_offer_timeout": "Ofè Ekspire",
    "baf_event_en_route": "Sou Chimen",
    "baf_event_arrived": "Rive",
    "baf_event_location_update": "Mizajou Kote",
    "baf_event_service_started": "Sèvis Kòmanse",
    "baf_event_service_completed": "Sèvis Fini",
    "baf_event_customer_confirmed": "Kliyan Konfime",
    "baf_event_tip_added": "Poubo Ajoute",
    "baf_event_tip_reminder": "Rapele Poubo",
    "baf_event_payout_approved": "Peman Apwouve",
    "baf_event_payout_completed": "Peman Fini",
    "baf_event_dispute_opened": "Pwosè Louvri",
    "baf_relative_just_now": "kounye a",
    "baf_customer_label": "Kliyan",
    "baf_vendor_label": "Moun Lavalas",
    "wl_title": "Potansyèl Sou Entènèt",
    "wl_subtitle": "Antre soti nan washpros.io — senkronize atravè Zapier",
    "wl_refresh": "Mizajou",
    "wl_total": "Total",
    "wl_today": "Jodi a",
    "wl_new": "Nouvo",
    "wl_contacted": "Kontakte",
    "wl_converted": "Konvèti",
    "wl_archived": "Achive",
    "wl_response_time_tracker": "Suivi Tan Repons",
    "wl_avg_response": "Repons Medyòm",
    "wl_fastest": "Pi Rapid",
    "wl_median": "Medyàn",
    "wl_slowest": "Pi Ralanti",
    "wl_pending_label": "an atant repons",
    "wl_trend_label": "Tan Repons Mwayèn Chak Jou (30 dènye jou yo)",
    "wl_simulator": "Similatè Nòt Potansyèl",
    "wl_simulator_hint": "Teste nòt san anrejistre",
    "wl_form_type": "Kalite Fòm",
    "wl_name": "Non",
    "wl_email": "Imèl",
    "wl_phone": "Telefòn",
    "wl_organization": "Òganizasyon",
    "wl_message": "Mesaj (mot kle machin yo bay nòt)",
    "wl_simulate": "Simile",
    "wl_search_placeholder": "Chèche non, imèl, telefòn...",
    "wl_all_types": "Tout Kalite",
    "wl_all_statuses": "Tout Estati",
    "wl_sort_score": "Triye: Nòt",
    "wl_sort_newest": "Triye: Pi Nouvo",
    "wl_filter_customer_waitlist": "Lis tann Kliyan",
    "wl_filter_vendor_signup": "Enskripsyon Vann",
    "wl_filter_contact": "Fòm Kontak",
    "wl_filter_fleet_inquiry": "Demann Flòt",
    "wl_no_leads": "Pa gen kontak",
    "wl_no_leads_hint": "Vize webhook Zapier ou a nan adrès ki pi ba a",
    "wl_th_date": "Dat",
    "wl_th_name": "Non",
    "wl_th_email": "Imèl",
    "wl_th_type": "Kalite",
    "wl_th_score": "Nòt",
    "wl_th_status": "Estati",
    "wl_th_actions": "Aksyon",
    "wl_action_contacted": "Kontakte",
    "wl_action_convert": "Konvèti",
    "wl_auto_converted": "Konvèti otomatikman",
    "wl_webhook_endpoint": "Zapier / Formspree Endpoint pou Webhook",
    "wl_lead_quick_action": "Kontak Sit wèb",
    "wl_lead_quick_action_sub": "enskripsyon washpros.io",
    "scc_title": "Sant Kontwòl Sistèm",
    "scc_subtitle": "Pòt dèyè master admin - Konplè kontwòl sistèm",
    "scc_save_all": "Sove Tout Chanjman",
    "scc_admin_only_title": "Aksè pou Administratè sèlman",
    "scc_admin_only_desc": "Chanjman isit la afekte tout platfòm nan. Fè atansyon lè w ap modifye paramèt pwodiksyon yo.",
    "scc_tab_payments": "Peman",
    "scc_tab_pricing": "Pri",
    "scc_tab_integrations": "Entegrasyon",
    "scc_tab_features": "Karakteristik",
    "scc_tab_system": "Sistèm",
    "scc_tab_team": "Aksè Ekip",
    "scc_tab_users": "Itilizatè"
  },
};

export default ADMIN_FRONT_PAGE_I18N;
